import { createFileRoute, Link } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { ArrowRight } from "lucide-react";
import { COMPARE_PAGE } from "@/content/marketing";
import {
  compareBreadcrumbJsonLd,
  compareJsonLd,
  compareRouteHead,
} from "@/lib/compare-seo";
import { jsonLdScript } from "@/lib/marketing-jsonld";
import "@/styles/marketing-pages.css";

// The hub's prefix in `APP_HOST_HUBS` (functions/src/posts/appHostPrefix.ts):
// every published app-host article under /compare/ is listed here.
const PREFIX = "compare";

// Same edge policy as the blog index: short shared TTL, long revalidate.
const CACHE = "public, s-maxage=60, stale-while-revalidate=86400";

// Postgres is read on the SSR server; the loader module is imported inside the
// handler so pg stays out of the client bundle.
const getCompareArticles = createServerFn({ method: "GET" }).handler(
  async () => {
    const { loadAppHostHubArticles } = await import(
      "@/lib/seo/appHostArticles"
    );
    return loadAppHostHubArticles(PREFIX);
  },
);

export const Route = createFileRoute("/_home/compare")({
  ssr: true, // public SEO route (D-216-5)
  loader: async () => ({ articles: await getCompareArticles() }),
  headers: () => ({ "cache-control": CACHE }),
  head: compareRouteHead,
  component: ComparePage,
});

function ComparePage() {
  const { articles } = Route.useLoaderData();
  const c = COMPARE_PAGE;
  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: jsonLdScript(compareJsonLd(articles)) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: jsonLdScript(compareBreadcrumbJsonLd),
        }}
      />
      <nav aria-label="Breadcrumb" className="sr-only">
        <ol>
          <li>
            <Link to="/">Home</Link>
          </li>
          <li aria-current="page">Compare</li>
        </ol>
      </nav>
      <main className="gs-marketing-page gs-compare-page">
        <section className="gs-marketing-hero">
          <p className="gs-eyebrow">{c.eyebrow}</p>
          <h1>{c.title}</h1>
          <p className="gs-marketing-lede">{c.lede}</p>
        </section>
        <section className="gs-marketing-section">
          {articles.length === 0 ? (
            <p>{c.empty}</p>
          ) : (
            <ul className="gs-compare-list">
              {articles.map((article) => (
                <li key={article.path}>
                  {/* A plain anchor: the article is served by the SSR
                      middleware, not by a router route. */}
                  <a href={article.path}>
                    <h2>{article.title}</h2>
                    {article.summary ? <p>{article.summary}</p> : null}
                    <span className="gs-text-link">
                      {c.readLabel}
                      <ArrowRight size={15} aria-hidden />
                    </span>
                  </a>
                </li>
              ))}
            </ul>
          )}
        </section>
      </main>
    </>
  );
}
